// src/pages/RetailPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SEOHead from "../components/SEOHead";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import {
  ArrowRight, CheckCircle, Zap, DollarSign, Clock,
  ShoppingBag, Users, BarChart2, Package, TrendingUp, MessageSquare,
} from "lucide-react";
import { motion } from "framer-motion";
import { useLanguage } from "../hooks/useLanguage";

const PAIN_ICONS = [Package, Users, Clock, TrendingUp, BarChart2, MessageSquare];
const PAIN_COLORS = [
  { color: "text-red-400", bg: "bg-red-500/10", border: "border-red-500/20" },
  { color: "text-orange-400", bg: "bg-orange-500/10", border: "border-orange-500/20" },
  { color: "text-yellow-400", bg: "bg-yellow-500/10", border: "border-yellow-500/20" },
  { color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/20" },
  { color: "text-purple-400", bg: "bg-purple-500/10", border: "border-purple-500/20" },
  { color: "text-[#3b82f6]", bg: "bg-blue-500/10", border: "border-blue-500/20" },
];
const TOOL_META = [
  { score: 92, color: "bg-blue-500" },
  { score: 87, color: "bg-purple-500" },
  { score: 85, color: "bg-blue-500" },
  { score: 81, color: "bg-orange-500" },
];

const CONTENT = {
  en: {
    title: "AI Blueprint for Retail Stores — Reduce Overstock, Grow Loyalty, Save Time",
    metaDesc: "Get a personalized AI implementation blueprint for your retail store. Smarter inventory, automated marketing, and more repeat customers. Built from 50K+ retail store data.",
    heroBadge: "Built for retail store owners",
    heroTitlePart1: "Which AI Tools Will Grow",
    heroTitlePart2: "Your Retail Store?",
    heroSubtitlePre: "Answer 8 questions about your store. Get a personalized blueprint with the",
    heroSubtitleBold: "exact tools, setup order, and ROI projections",
    heroSubtitlePost: "— built from",
    heroSubtitleEnd: "vetted AI tools and 2026 retail industry benchmarks.",
    heroBullets: [
      "Cut overstock and stockouts with AI demand forecasting",
      "Grow repeat purchases from 27% to 44—56% with AI loyalty flows",
      "Reduce scheduling and admin time by 80% — reclaim 5+ hours/week",
      "Drive consistent foot traffic with AI-powered marketing on autopilot",
    ],
    heroCta: "Fix My Inventory Guesswork",
    heroCtaFine: "One-time — No subscription — Ready in minutes",
    painEyebrow: "Sound familiar?",
    painHeading: "The 6 Profit Leaks in Most Retail Stores",
    painSub: "AI can fix all of these — but only if you know which tools to use and in what order. That's what the blueprint tells you.",
    painPoints: [
      { title: "Inventory guesswork causing overstock and stockouts", desc: "Retailers lose $1.75 trillion/year to inventory distortion. AI demand forecasting cuts overstock by 30% and stockouts by 65% — protecting margin on both sides.", stat: "65% fewer stockouts" },
      { title: "Customer loyalty is weak", desc: "Acquiring a new customer costs 5x more than keeping one. AI-powered loyalty programs increase repeat purchase rates from 27% to 44—56%.", stat: "56% repeat rate possible" },
      { title: "Staff scheduling is a weekly headache", desc: "Retail managers spend 4+ hours/week on scheduling. AI scheduling tools reduce this to 30 minutes and cut labor cost overages by 21%.", stat: "21% labor savings" },
      { title: "Marketing doesn't drive consistent foot traffic", desc: "Retailers with consistent omnichannel marketing get 3x more foot traffic than those posting sporadically. AI tools maintain presence with 30 min/week of effort.", stat: "3x foot traffic lift" },
      { title: "No visibility into what's actually selling", desc: "56% of retail owners make reorder decisions without real data. AI analytics tools show you margin by SKU, velocity by category, and what to cut.", stat: "56% decide without data" },
      { title: "Customer questions eat into store hours", desc: "Staff spends 2+ hours/day answering the same questions — hours, returns, product availability. AI handles 70% of these automatically, freeing your team to sell.", stat: "70% queries automated" },
    ],
    toolEyebrow: "Tool stack preview",
    toolHeading: "Tools Your Blueprint Could Recommend",
    toolSubPre: "These are examples. Your blueprint picks from",
    toolSubPost: "+ tools based on your store type, size, and budget.",
    toolStack: [
      { name: "Klaviyo", category: "Email & SMS Marketing", price: "From $45/mo", why: "AI segmentation and behavioral triggers. Loyalty re-engagement campaigns average 3.4x ROI for retail. Integrates with Square, Shopify, and most POS systems." },
      { name: "Tidio", category: "Customer Support AI", price: "Free — $49/mo", why: "AI chat handles 70% of common retail questions automatically — store hours, return policy, product availability. Free tier works for most stores." },
      { name: "Inventory Planner", category: "Demand Forecasting", price: "From $99/mo", why: "AI-powered inventory optimization. Predicts stockouts 30 days ahead and auto-generates reorder suggestions. Pays for itself in the first overstock prevented." },
      { name: "Homebase", category: "Retail Staff Scheduling", price: "Free — $24.95/mo", why: "Built for hourly retail teams. AI-assisted scheduling, time clock, and labor cost forecasting. Free tier for a single location works for most independent retailers. Cuts scheduling time from 4 hours to 30 minutes per week." },
    ],
    matchLabel: "match",
    toolFootnotePre: "Your blueprint selects 5—8 tools from a database of",
    toolFootnotePost: "+, ranked for your specific store.",
    getEyebrow: "What you get",
    getHeadingPre: "Your $",
    getHeadingPost: " Retail AI Blueprint Includes",
    whatYouGet: [
      { title: "Your exact retail AI tool stack", desc: "5—8 tools matched to your store type, size, and specific problems. Whether you're a solo owner or managing a team of 20." },
      { title: "12-week implementation roadmap", desc: "Phase 1 targets your biggest profit leak — usually inventory or customer retention. Week-by-week, not overwhelming." },
      { title: "Custom prompt library for retail", desc: "15—30 prompts for product descriptions, email campaigns, social posts, staff communications, and promotional copy." },
      { title: "ROI projections for your store", desc: "Expected inventory savings, marketing revenue lift, and hours saved per week — built from 2026 retail benchmarks and vetted AI tool data." },
      { title: "Risk guide and what to avoid", desc: "Common mistakes when retail stores adopt too many tools at once. How to sequence implementations to protect daily operations." },
      { title: "First 30 days checklist", desc: "Day-by-day tasks to see real results in your first month — reduced overstock, more repeat customers, or consistent marketing." },
    ],
    bottomHeading1: "Stop Guessing on Inventory and Marketing.",
    bottomHeading2: "Start Running a Smarter Store.",
    bottomSub: "Answer 8 questions about your store. Get a blueprint with your exact tool stack, setup order, and week-by-week roadmap. Ready in minutes.",
    bottomCta: "Get My Retail AI Blueprint",
    bottomFineReady: "Ready in minutes",
    bottomFineOneTime: "One-time $",
    bottomFineNoSub: "No subscription",
    guarantee: "60-day money-back guarantee if it doesn't deliver value.",
    preview: {
      eyebrow: "AI Implementation Blueprint",
      kicker: "Retail Store — Brick & Mortar",
      reportReady: "✓ Report Ready",
      roiLabel: "Year 1 ROI",
      hoursSavedLabel: "Saved/week",
      toolsLabel: "Recommended",
      hrsUnit: "hrs",
      toolsUnit: "tools",
      topToolsEyebrow: "Top Tools For Your Store",
      toolBadges: ["Email — $45/mo", "Support — Free", "Inventory — $99/mo"],
      matchShort: "match",
      roadmapEyebrow: "12-Week Roadmap",
      phaseLabels: ["Phase 1\nInventory & Loyalty", "Phase 2\nMarketing", "Phase 3\nOperations"],
      floatingBadge1: "✨ Built in 3 min",
      floatingBadge2: "Retail-optimized",
    },
    faqEyebrow: "Frequently asked",
    faqHeading: "Before you build your blueprint",
    faq: [
      { q: "I run a single-location boutique / multi-store / apparel / grocery / specialty. Does this still apply?", a: "Yes. The blueprint asks about your format, SKU count, and biggest pain, then tailors the stack. Boutiques usually get loyalty and marketing AI; multi-store gets inventory-sync and demand-forecasting tools; specialty retail gets content and recommendation tools; grocery gets waste-reduction and basket-analysis tools." },
      { q: "How long does the blueprint take to implement?", a: "It's a 12-week roadmap built for owner-operators with 2—4 hours a week. Phase 1 targets your biggest leak first — usually overstock, slow marketing, or loyalty leakage — and most stores see measurable results in 2—3 weeks before Phase 2." },
      { q: "I've tried AI tools and they didn't move the needle. Why is this different?", a: "Most retailers pick a tool before defining the bottleneck, skip the POS or ecomm integration step, and never measure margin impact. The blueprint flips that: ranks tools by fit to your format, maps each to your POS (Lightspeed, Square, Shopify POS, Clover, etc.) and projects ROI before you spend a dollar." },
      { q: "Is my customer and sales data safe?", a: "Every tool recommendation includes a data-handling note — where customer data is stored, PCI posture for anything touching payments, and whether the vendor shares data with third parties. Tools that train on customer data by default are flagged, not quietly recommended." },
      { q: "What if the blueprint doesn't fit my store?", a: "60-day money-back guarantee, no questions asked. Email us within 60 days and we refund in full. You keep the blueprint. We'd rather you leave happy than keep $149 on a report you couldn't use." },
    ],
  },
  es: {
    title: "Blueprint de IA para Tiendas Minoristas — Reduce Sobrestock, Fideliza y Ahorra Tiempo",
    metaDesc: "Recibe un blueprint personalizado de implementación de IA para tu tienda minorista. Inventario más inteligente, marketing automatizado y más clientes recurrentes. Creado con datos de más de 50.000 tiendas.",
    heroBadge: "Diseñado para dueños de tiendas minoristas",
    heroTitlePart1: "¿Qué Herramientas de IA Harán Crecer",
    heroTitlePart2: "Tu Tienda Minorista?",
    heroSubtitlePre: "Responde 8 preguntas sobre tu tienda. Obtén un blueprint personalizado con las",
    heroSubtitleBold: "herramientas exactas, el orden de implementación y las proyecciones de ROI",
    heroSubtitlePost: "— construido a partir de",
    heroSubtitleEnd: "herramientas de IA validadas y benchmarks del sector minorista 2026.",
    heroBullets: [
      "Reduce sobrestock y quiebres de stock con pronóstico de demanda por IA",
      "Eleva las compras repetidas del 27% al 44—56% con flujos de fidelización por IA",
      "Recorta el tiempo de agenda y administración un 80% — recupera 5+ horas/semana",
      "Impulsa tráfico constante a la tienda con marketing automático por IA",
    ],
    heroCta: "Deja de Adivinar Mi Inventario",
    heroCtaFine: "Pago único — Sin suscripción — Listo en minutos",
    painEyebrow: "¿Te suena familiar?",
    painHeading: "Las 6 Fugas de Ganancia en la Mayoría de Tiendas",
    painSub: "La IA puede resolver todo esto — pero solo si sabes qué herramientas usar y en qué orden. Eso es lo que te dice el blueprint.",
    painPoints: [
      { title: "Inventario al tanteo provoca sobrestock y quiebres", desc: "El sector minorista pierde $1.75 billones al año por distorsión de inventario. El pronóstico de demanda por IA reduce el sobrestock un 30% y los quiebres un 65% — protegiendo el margen en ambos lados.", stat: "65% menos quiebres" },
      { title: "La lealtad del cliente es débil", desc: "Conseguir un cliente nuevo cuesta 5x más que retener uno. Los programas de lealtad con IA elevan la tasa de compra repetida del 27% al 44—56%.", stat: "56% de recompra posible" },
      { title: "Programar al personal es un dolor de cabeza semanal", desc: "Los gerentes de retail dedican 4+ horas/semana a armar turnos. Las herramientas de IA lo reducen a 30 minutos y recortan los sobrecostos laborales un 21%.", stat: "21% ahorro laboral" },
      { title: "El marketing no genera tráfico constante", desc: "Las tiendas con marketing omnicanal consistente reciben 3x más tráfico que las que publican a ratos. Las herramientas de IA mantienen la presencia con 30 min a la semana.", stat: "3x más tráfico" },
      { title: "Sin visibilidad de lo que realmente se vende", desc: "El 56% de los dueños decide reabastecer sin datos reales. Las herramientas de análisis con IA muestran margen por SKU, velocidad por categoría y qué cortar.", stat: "56% decide sin datos" },
      { title: "Las consultas de clientes comen horas de tienda", desc: "El personal dedica 2+ horas/día a responder las mismas preguntas — horarios, devoluciones, disponibilidad. La IA resuelve el 70% automáticamente, liberando al equipo para vender.", stat: "70% consultas automatizadas" },
    ],
    toolEyebrow: "Vista previa del stack",
    toolHeading: "Herramientas Que Tu Blueprint Podría Recomendar",
    toolSubPre: "Son ejemplos. Tu blueprint elige entre",
    toolSubPost: "+ herramientas según el tipo de tienda, tamaño y presupuesto.",
    toolStack: [
      { name: "Klaviyo", category: "Email y SMS Marketing", price: "Desde $45/mes", why: "Segmentación con IA y disparadores por comportamiento. Las campañas de reactivación de fidelización tienen un ROI promedio de 3.4x en retail. Integra con Square, Shopify y la mayoría de POS." },
      { name: "Tidio", category: "Atención al Cliente con IA", price: "Gratis — $49/mes", why: "El chat con IA responde el 70% de las consultas comunes automáticamente — horarios, políticas de devolución, disponibilidad. El plan gratuito funciona para la mayoría de tiendas." },
      { name: "Inventory Planner", category: "Pronóstico de Demanda", price: "Desde $99/mes", why: "Optimización de inventario con IA. Anticipa quiebres con 30 días de antelación y sugiere reposiciones automáticamente. Se paga sola con el primer sobrestock evitado." },
      { name: "Homebase", category: "Agenda de Personal Retail", price: "Gratis — $24.95/mes", why: "Hecho para equipos retail por hora. Agenda asistida por IA, reloj checador y pronóstico de costo laboral. El plan gratuito para una sola tienda funciona para la mayoría de retailers independientes. Reduce el tiempo de agenda de 4 horas a 30 minutos a la semana." },
    ],
    matchLabel: "compatibilidad",
    toolFootnotePre: "Tu blueprint selecciona 5—8 herramientas de una base de datos de",
    toolFootnotePost: "+, clasificadas para tu tienda específica.",
    getEyebrow: "Lo que obtienes",
    getHeadingPre: "Tu Blueprint de IA Retail por $",
    getHeadingPost: " Incluye",
    whatYouGet: [
      { title: "Tu stack exacto de herramientas de IA para retail", desc: "5—8 herramientas elegidas según tu tipo de tienda, tamaño y problemas específicos. Ya sea que trabajes solo o lideres un equipo de 20." },
      { title: "Hoja de ruta de 90 días", desc: "La Fase 1 apunta a tu mayor fuga de ganancia — normalmente inventario o retención. Semana a semana, sin abrumarte." },
      { title: "Biblioteca de prompts para retail", desc: "15—30 prompts para descripciones de producto, campañas de email, posts sociales, comunicaciones al personal y copy promocional." },
      { title: "Proyecciones de ROI para tu tienda", desc: "Ahorros de inventario esperados, aumento de ingresos por marketing y horas ahorradas por semana — basado en benchmarks retail 2026 y datos validados de herramientas de IA." },
      { title: "Guía de riesgos y qué evitar", desc: "Errores comunes cuando las tiendas adoptan demasiadas herramientas a la vez. Cómo secuenciar las implementaciones para proteger la operación diaria." },
      { title: "Checklist de los primeros 30 días", desc: "Tareas día a día para ver resultados reales en tu primer mes — menos sobrestock, más clientes recurrentes o marketing consistente." },
    ],
    bottomHeading1: "Deja de Adivinar con Inventario y Marketing.",
    bottomHeading2: "Empieza a Dirigir una Tienda Más Inteligente.",
    bottomSub: "Responde 8 preguntas sobre tu tienda. Obtén un blueprint con tu stack exacto, orden de implementación y hoja de ruta semana a semana. Listo en minutos.",
    bottomCta: "Arma Mi Blueprint de Retail",
    bottomFineReady: "Listo en minutos",
    bottomFineOneTime: "Pago único $",
    bottomFineNoSub: "Sin suscripción",
    guarantee: "Garantía de devolución de 60 días si no aporta valor.",
    preview: {
      eyebrow: "Blueprint de Implementación de IA",
      kicker: "Tienda Minorista — Local Físico",
      reportReady: "✓ Informe Listo",
      roiLabel: "ROI Año 1",
      hoursSavedLabel: "Ahorro/semana",
      toolsLabel: "Recomendadas",
      hrsUnit: "hrs",
      toolsUnit: "herramientas",
      topToolsEyebrow: "Top Herramientas Para Tu Tienda",
      toolBadges: ["Email — $45/mes", "Soporte — Gratis", "Inventario — $99/mes"],
      matchShort: "match",
      roadmapEyebrow: "Hoja de Ruta 12 Semanas",
      phaseLabels: ["Fase 1\nInventario y Lealtad", "Fase 2\nMarketing", "Fase 3\nOperaciones"],
      floatingBadge1: "✨ Listo en 3 min",
      floatingBadge2: "Optimizado para retail",
    },
    faqEyebrow: "Preguntas frecuentes",
    faqHeading: "Antes de armar tu blueprint",
    faq: [
      { q: "Tengo una boutique de una sola tienda, multi-tienda, ropa, abarrotes o especialidad. ¿Igual aplica?", a: "Sí. El blueprint te pregunta tu formato, cantidad de SKUs y mayor dolor, y arma el stack a tu medida. Las boutiques reciben IA de lealtad y marketing; multi-tienda recibe sincronización de inventario y pronóstico de demanda; especialidad recibe contenido y recomendaciones; abarrotes recibe reducción de merma y análisis de canasta." },
      { q: "¿Cuánto tarda implementar el blueprint?", a: "Es una hoja de ruta de 12 semanas pensada para dueños-operadores con 2—4 horas a la semana. La Fase 1 ataca tu mayor fuga primero — normalmente sobrestock, marketing lento o fuga de lealtad — y la mayoría ve resultados medibles en 2—3 semanas antes de pasar a la Fase 2." },
      { q: "Ya probé herramientas de IA y no movieron la aguja. ¿Por qué esto es distinto?", a: "La mayoría compra la herramienta antes de definir el cuello de botella, saltan la integración con el POS o ecomm y nunca miden el impacto en margen. El blueprint invierte eso: rankea por ajuste a tu formato, mapea cada herramienta a tu POS (Lightspeed, Square, Shopify POS, Clover, etc.) y proyecta el ROI antes de que gastes un dólar." },
      { q: "¿Están seguros los datos de mis clientes y ventas?", a: "Cada recomendación incluye una nota de manejo de datos — dónde se almacenan, postura PCI para lo que toca pagos y si el proveedor comparte con terceros. Las herramientas que entrenan con datos de cliente por defecto se marcan, no se recomiendan en silencio." },
      { q: "¿Y si el blueprint no encaja con mi tienda?", a: "Garantía de devolución de 60 días, sin preguntas. Nos escribes dentro de los 60 días y te devolvemos todo. Te quedas con el blueprint. Preferimos que te vayas contento antes que cobrarte $149 por un reporte que no pudiste usar." },
    ],
  },
  tr: {
    title: "Perakende Mağazaları için Yapay Zeka Planı — Fazla Stoğu Azaltın, Sadakati Büyütün, Zaman Kazanın",
    metaDesc: "Perakende mağazanız için kişiselleştirilmiş bir yapay zeka uygulama planı alın. Daha akıllı envanter, otomatik pazarlama ve daha fazla tekrar eden müşteri. 50.000+ mağaza verisiyle hazırlandı.",
    heroBadge: "Perakende mağaza sahipleri için tasarlandı",
    heroTitlePart1: "Hangi Yapay Zeka Araçları",
    heroTitlePart2: "Perakende Mağazanızı Büyütür?",
    heroSubtitlePre: "Mağazanız hakkında 8 soruyu yanıtlayın.",
    heroSubtitleBold: "tam araç listesi, kurulum sırası ve ROI projeksiyonları",
    heroSubtitlePost: "içeren kişiselleştirilmiş bir plan edinin —",
    heroSubtitleEnd: "onaylı yapay zeka aracı ve 2026 perakende kıyaslamalarıyla hazırlandı.",
    heroBullets: [
      "Yapay zeka talep tahminiyle fazla stok ve stoksuz kalmayı azaltın",
      "Yapay zeka sadakat akışlarıyla tekrar alışverişi %27'den %44—56'ya çıkarın",
      "Agenda ve yönetim süresini %80 kısaltın — haftada 5+ saat kazanın",
      "Otomatik pilotta yapay zeka pazarlamayla mağaza ziyaretlerini artırın",
    ],
    heroCta: "Envanter Tahminini Düzeltin",
    heroCtaFine: "Tek seferlik — Abonelik yok — Dakikalar içinde hazır",
    painEyebrow: "Tanıdık geliyor mu?",
    painHeading: "Çoğu Perakende Mağazasındaki 6 Kâr Sızıntısı",
    painSub: "Yapay zeka bunların hepsini çözebilir — ama yalnızca hangi araçları hangi sırayla kullanacağınızı bilirseniz. Plan size tam olarak bunu söyler.",
    painPoints: [
      { title: "Tahmine dayalı envanter fazla stok ve stoksuz kalmaya yol açıyor", desc: "Perakende sektörü envanter sapmasından yılda $1,75 trilyon kaybediyor. Yapay zeka talep tahmini fazla stoğu %30, stoksuz kalmayı %65 azaltır — iki taraftan da marjı korur.", stat: "%65 daha az stoksuz kalma" },
      { title: "Müşteri sadakati zayıf", desc: "Yeni müşteri kazanmak, mevcut bir müşteriyi tutmaktan 5 kat pahalıdır. Yapay zeka destekli sadakat programları tekrar alışveriş oranını %27'den %44—56'ya çıkarır.", stat: "%56 tekrar alışveriş mümkün" },
      { title: "Personel çizelgesi her hafta baş ağrısı", desc: "Perakende yöneticileri haftada 4+ saatini çizelge oluşturmaya harcıyor. Yapay zeka araçları bunu 30 dakikaya indirir ve işçilik aşımlarını %21 azaltır.", stat: "%21 işçilik tasarrufu" },
      { title: "Pazarlama tutarlı mağaza ziyareti getirmiyor", desc: "Tutarlı çok kanallı pazarlama yapan mağazalar, seyrek paylaşanlardan 3 kat daha fazla ziyaret alır. Yapay zeka araçları haftada 30 dakikayla varlığı canlı tutar.", stat: "3 kat ziyaret artışı" },
      { title: "Gerçekten ne sattığına dair görünürlük yok", desc: "Perakendecilerin %56'sı gerçek veri olmadan sipariş kararı veriyor. Yapay zeka analiz araçları SKU bazında marjı, kategori bazında hızı ve neyin çıkarılacağını gösterir.", stat: "%56 veri olmadan karar veriyor" },
      { title: "Müşteri soruları mağaza saatlerini yiyor", desc: "Personel, aynı soruları yanıtlamak için günde 2+ saatini harcıyor — saatler, iadeler, ürün mevcudiyeti. Yapay zeka bunların %70'ini otomatik çözer ve ekibinizi satışa serbest bırakır.", stat: "%70 sorgu otomatik" },
    ],
    toolEyebrow: "Araç önizlemesi",
    toolHeading: "Planınızın Önerebileceği Araçlar",
    toolSubPre: "Bunlar örnek. Planınız, mağaza tipinize, büyüklüğünüze ve bütçenize göre",
    toolSubPost: "+ araç arasından seçim yapar.",
    toolStack: [
      { name: "Klaviyo", category: "E-posta ve SMS Pazarlama", price: "Aylık $45'ten başlar", why: "Yapay zeka segmentasyonu ve davranışsal tetikleyiciler. Perakende için sadakat yeniden etkileşim kampanyaları ortalama 3,4x ROI sağlar. Square, Shopify ve çoğu POS sistemiyle entegredir." },
      { name: "Tidio", category: "Yapay Zeka Müşteri Desteği", price: "Ücretsiz — $49/ay", why: "Yapay zeka sohbet, yaygın perakende sorularının %70'ini otomatik yanıtlar — mağaza saatleri, iade politikası, ürün mevcudiyeti. Ücretsiz paket çoğu mağazada işe yarar." },
      { name: "Inventory Planner", category: "Talep Tahmini", price: "Aylık $99'dan başlar", why: "Yapay zeka destekli envanter optimizasyonu. Stoksuz kalmayı 30 gün önceden tahmin eder ve sipariş önerilerini otomatik üretir. Önlediği ilk fazla stokla kendini amorti eder." },
      { name: "Homebase", category: "Perakende Personel Çizelgesi", price: "Ücretsiz — Aylık $24.95'a kadar", why: "Saatlik perakende ekipleri için tasarlandı. Yapay zeka destekli çizelge, mesai takibi ve işçilik maliyeti tahmini. Tek lokasyon için ücretsiz paket çoğu bağımsız perakendeci için yeterli. Çizelge süresini haftada 4 saatten 30 dakikaya indirir." },
    ],
    matchLabel: "uyum",
    toolFootnotePre: "Planınız, mağazanıza göre sıralanmış",
    toolFootnotePost: "+ araçlık veri tabanından 5—8 araç seçer.",
    getEyebrow: "Neler alıyorsunuz",
    getHeadingPre: "$",
    getHeadingPost: "'lık Perakende Yapay Zeka Planınız Şunları İçerir",
    whatYouGet: [
      { title: "Tam perakende yapay zeka araç listeniz", desc: "Mağaza tipinize, büyüklüğünüze ve sorunlarınıza göre seçilmiş 5—8 araç. Tek başınıza çalışıyor olun ya da 20 kişilik bir ekip yönetin." },
      { title: "90 günlük uygulama yol haritası", desc: "Faz 1, en büyük kâr sızıntınızı hedefler — genellikle envanter ya da müşteri tutma. Hafta hafta, bunaltıcı değil." },
      { title: "Perakendeye özel prompt kütüphanesi", desc: "Ürün açıklamaları, e-posta kampanyaları, sosyal paylaşımlar, personel iletişimi ve promosyon metinleri için 15—30 prompt." },
      { title: "Mağazanız için ROI projeksiyonları", desc: "Beklenen envanter tasarrufu, pazarlama geliri artışı ve haftalık kazanılan saatler — 2026 perakende kıyaslamalarına ve onaylı yapay zeka aracı verisine dayanır." },
      { title: "Risk rehberi ve nelerden kaçınmalı", desc: "Mağazalar aynı anda çok fazla araç benimsediğinde yapılan yaygın hatalar. Günlük operasyonu korumak için uygulamaları nasıl sıralayacağınız." },
      { title: "İlk 30 gün kontrol listesi", desc: "İlk ayınızda somut sonuçlar için gün gün görevler — daha az fazla stok, daha çok tekrar eden müşteri ya da tutarlı pazarlama." },
    ],
    bottomHeading1: "Envanter ve Pazarlamada Tahmin Etmeyi Bırakın.",
    bottomHeading2: "Daha Akıllı Bir Mağaza Yönetmeye Başlayın.",
    bottomSub: "Mağazanız hakkında 8 soruyu yanıtlayın. Tam araç listeniz, kurulum sıranız ve hafta hafta yol haritanızla planınızı alın. Dakikalar içinde hazır.",
    bottomCta: "Perakende AI Blueprint'imi Al",
    bottomFineReady: "Dakikalar içinde hazır",
    bottomFineOneTime: "Tek seferlik $",
    bottomFineNoSub: "Abonelik yok",
    guarantee: "Değer vermezse 60 gün içinde para iadesi garantisi.",
    preview: {
      eyebrow: "Yapay Zeka Uygulama Planı",
      kicker: "Perakende Mağazası — Fiziksel",
      reportReady: "✓ Rapor Hazır",
      roiLabel: "1. Yıl ROI",
      hoursSavedLabel: "Haftalık tasarruf",
      toolsLabel: "Önerilen",
      hrsUnit: "saat",
      toolsUnit: "araç",
      topToolsEyebrow: "Mağazanız İçin En İyi Araçlar",
      toolBadges: ["E-posta — $45/ay", "Destek — Ücretsiz", "Envanter — $99/ay"],
      matchShort: "uyum",
      roadmapEyebrow: "12 Haftalık Yol Haritası",
      phaseLabels: ["Faz 1\nEnvanter ve Sadakat", "Faz 2\nPazarlama", "Faz 3\nOperasyonlar"],
      floatingBadge1: "✨ 3 dakikada hazır",
      floatingBadge2: "Perakendeye özel",
    },
    faqEyebrow: "Sıkça sorulanlar",
    faqHeading: "Blueprint'inizi oluşturmadan önce",
    faq: [
      { q: "Tek mağazalı butik / çok şubeli / giyim / market / uzman perakende işletiyorum. Yine de işe yarar mı?", a: "Evet. Blueprint size formatınızı, SKU sayınızı ve en büyük sıkıntınızı sorar ve stack'i buna göre özelleştirir. Butikler genellikle sadakat ve pazarlama AI'ı alır; çok şubeli mağazalar envanter senkron ve talep tahmin araçları alır; uzman perakende içerik ve öneri araçları alır; market zayiat azaltma ve sepet analiz araçları alır." },
      { q: "Blueprint'i uygulamak ne kadar sürer?", a: "Haftada 2—4 saat ayırabilen sahip-işletmeciler için tasarlanmış 12 haftalık bir yol haritasıdır. 1. Faz önce en büyük kaybınıza — genellikle fazla stok, yavaş pazarlama veya sadakat sızıntısı — odaklanır ve çoğu mağaza 2. Faz'a geçmeden önce 2—3 hafta içinde ölçülebilir sonuç görür." },
      { q: "AI araçlarını denedim ama iğneyi oynatmadı. Bunun farkı ne?", a: "Çoğu perakendeci darboğazı tanımlamadan aracı seçer, POS veya e-ticaret entegrasyonunu atlar ve marj etkisini hiç ölçmez. Blueprint bunu tersine çevirir: araçları formatınıza uygunluğa göre sıralar, her birini POS'unuza (Lightspeed, Square, Shopify POS, Clover vb.) eşler ve bir kuruş harcamadan önce ROI öngörür." },
      { q: "Müşteri ve satış verilerim güvende mi?", a: "Her öneride veri işleme notu vardır — verilerin nerede tutulduğu, ödemelere dokunan şeyler için PCI duruşu ve sağlayıcının üçüncü taraflarla paylaşıp paylaşmadığı. Varsayılan olarak müşteri verisiyle eğitim yapan araçlar işaretlenir, sessizce önerilmez." },
      { q: "Blueprint mağazama uymazsa ne olur?", a: "60 gün içinde koşulsuz para iade garantisi. 60 gün içinde bize yazarsınız, tam iade yaparız. Blueprint sizde kalır. 149 dolarınızı tutup mutsuz göndermektense kullanamayacağınız bir rapor için iade etmeyi tercih ederiz." },
    ],
  },
};

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.08 } } };

const RetailBlueprintPreview = ({ c }) => (
  <div className="relative select-none">
    <div className="absolute -inset-4 bg-gradient-to-br from-blue-600/20 via-teal-600/20 to-cyan-600/20 rounded-3xl blur-2xl" />
    <div className="relative rounded-2xl overflow-hidden bg-slate-800/90 border border-white/10 shadow-2xl backdrop-blur-sm">
      <div className="bg-slate-900 px-5 py-3.5 border-b border-white/10 flex items-center justify-between">
        <div>
          <div className="text-[10px] text-[#3b82f6] font-semibold tracking-widest uppercase mb-0.5">{c.preview.eyebrow}</div>
          <div className="text-sm font-bold text-white">{c.preview.kicker}</div>
        </div>
        <span className="text-[10px] font-semibold bg-blue-500/20 text-[#3b82f6] border border-blue-500/30 px-2.5 py-1 rounded-full">{c.preview.reportReady}</span>
      </div>
      <div className="grid grid-cols-3 border-b border-white/10">
        {[["$8–36K", c.preview.roiLabel], [`5 ${c.preview.hrsUnit}`, c.preview.hoursSavedLabel], [`5–8 ${c.preview.toolsUnit}`, c.preview.toolsLabel]].map(([val, lab]) => (
          <div key={lab} className="px-4 py-3 text-center border-r last:border-r-0 border-white/10">
            <div className="text-base font-bold text-[#3b82f6]">{val}</div>
            <div className="text-[10px] text-slate-500 mt-0.5">{lab}</div>
          </div>
        ))}
      </div>
      <div className="p-4 space-y-2.5">
        <div className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold mb-2.5">{c.preview.topToolsEyebrow}</div>
        {[
          { name: "Klaviyo", badge: c.preview.toolBadges[0], score: 92, color: "bg-blue-500" },
          { name: "Tidio", badge: c.preview.toolBadges[1], score: 87, color: "bg-purple-500" },
          { name: "Inv. Planner", badge: c.preview.toolBadges[2], score: 85, color: "bg-blue-500" },
        ].map((tool) => (
          <div key={tool.name} className="bg-slate-900/80 rounded-xl p-3 border border-white/10/40">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-semibold text-white">{tool.name}</span>
              <span className="text-[10px] text-slate-400">{tool.badge}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                <motion.div className={`h-full ${tool.color} rounded-full`} initial={{ width: 0 }} animate={{ width: `${tool.score}%` }} transition={{ duration: 1.2, delay: 0.5, ease: "easeOut" }} />
              </div>
              <span className="text-[10px] text-slate-500 flex-shrink-0">{tool.score}% {c.preview.matchShort}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="px-4 pb-4">
        <div className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold mb-2.5">{c.preview.roadmapEyebrow}</div>
        <div className="flex gap-2">
          {c.preview.phaseLabels.map((p, i) => (
            <div key={p} className={`flex-1 text-center rounded-lg py-2 px-1 text-[10px] font-medium border ${i === 0 ? "bg-blue-500/20 border-blue-500/40 text-[#3b82f6]" : "bg-white/8 border-white/10 text-slate-500"}`}>
              {p.split("\n").map((line, j) => <div key={j}>{line}</div>)}
            </div>
          ))}
        </div>
      </div>
    </div>
    <motion.div animate={{ y: [0, -6, 0] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }} className="absolute -top-4 -right-4 bg-blue-500 text-white text-xs font-bold px-3 py-1.5 rounded-xl shadow-lg shadow-blue-500/40">
      {c.preview.floatingBadge1}
    </motion.div>
    <motion.div animate={{ y: [0, 6, 0] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }} className="absolute -bottom-3 -left-4 bg-teal-600 text-white text-xs font-bold px-3 py-1.5 rounded-xl shadow-lg shadow-teal-600/40">
      {c.preview.floatingBadge2}
    </motion.div>
  </div>
);

const RetailPage = () => {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const c = CONTENT[lang] || CONTENT.en;
  const [toolCount, setToolCount] = useState(null);
  const displayCount = toolCount || 348;
  const [currentPrice, setCurrentPrice] = useState(149);

  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL || 'https://ai-workshop-llc-backend.onrender.com';
    fetch(`${base}/api/tools/stats`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d?.total) setToolCount(d.total); })
      .catch(() => {});
    fetch(`${base}/api/pricing/current`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d?.price) setCurrentPrice(d.price); })
      .catch(() => {});
  }, []);

  const goToAudit = () => navigate("/audit?industry=retail");

  return (
    <div className="min-h-screen bg-[#0f172a] text-white">
      <SEOHead
        path="/retail"
        lang={lang}
        title={c.title}
        description={c.metaDesc}
        faqItems={c.faq || c.faqs}
        industry="Retail"
      />
      <Header />

      {/* HERO */}
      <section className="relative pt-20 pb-24 px-4 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-blue-500/10 rounded-full filter blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-teal-600/10 rounded-full filter blur-[120px]" />
          <div className="absolute inset-0 opacity-[0.025]" style={{ backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        </div>
        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 text-[#3b82f6] text-sm font-semibold px-4 py-2 rounded-full mb-8">
                <span className="text-base">🛍️</span> {c.heroBadge}
              </motion.div>
              <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight mb-6">
                <span className="text-white">{c.heroTitlePart1}</span>
                <br />
                <span className="bg-gradient-to-r from-[#3b82f6] via-teal-400 to-cyan-400 bg-clip-text text-transparent">{c.heroTitlePart2}</span>
              </motion.h1>
              <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="text-lg text-slate-400 mb-8 leading-relaxed max-w-xl">
                {c.heroSubtitlePre}{" "}
                <span className="text-white font-semibold">{c.heroSubtitleBold}</span>{" "}
                {c.heroSubtitlePost} {displayCount} {c.heroSubtitleEnd}
              </motion.p>
              <motion.ul initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="space-y-3 mb-10">
                {c.heroBullets.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#3b82f6] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-300 text-sm">{item}</span>
                  </li>
                ))}
              </motion.ul>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="flex flex-col sm:flex-row gap-4 items-start">
                <Button onClick={goToAudit} size="lg" className="bg-blue-500 hover:bg-[#3b82f6] text-slate-950 font-black text-lg px-10 py-6 rounded-2xl shadow-lg shadow-blue-500/30 transition-all hover:scale-[1.02] active:scale-[0.98]">
                  {c.heroCta} — ${currentPrice} <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <p className="text-xs text-slate-500 self-center">{c.heroCtaFine}</p>
              </motion.div>
            </div>
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.35, duration: 0.7 }} className="hidden lg:block">
              <RetailBlueprintPreview c={c} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* PAIN POINTS */}
      <section className="py-20 px-4 border-t border-white/8">
        <div className="max-w-6xl mx-auto">
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}>
            <motion.div variants={fadeUp} className="text-center mb-14">
              <div className="text-sm text-[#3b82f6] font-semibold tracking-widest uppercase mb-3">{c.painEyebrow}</div>
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">{c.painHeading}</h2>
              <p className="text-slate-400 max-w-2xl mx-auto">{c.painSub}</p>
            </motion.div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {c.painPoints.map((pp, idx) => {
                const Icon = PAIN_ICONS[idx];
                const cls = PAIN_COLORS[idx];
                return (
                  <motion.div key={pp.title} variants={fadeUp} className={`rounded-2xl border p-6 ${cls.bg} ${cls.border}`}>
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${cls.bg} border ${cls.border} mb-4`}>
                      <Icon className={`w-5 h-5 ${cls.color}`} />
                    </div>
                    <h3 className="text-white font-bold text-base mb-2">{pp.title}</h3>
                    <p className="text-slate-400 text-sm mb-3 leading-relaxed">{pp.desc}</p>
                    <div className={`text-xs font-bold ${cls.color}`}>{pp.stat}</div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* TOOL PREVIEW */}
      <section className="py-20 px-4 border-t border-white/8 bg-slate-900/30">
        <div className="max-w-5xl mx-auto">
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}>
            <motion.div variants={fadeUp} className="text-center mb-14">
              <div className="text-sm text-[#3b82f6] font-semibold tracking-widest uppercase mb-3">{c.toolEyebrow}</div>
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">{c.toolHeading}</h2>
              <p className="text-slate-400 max-w-xl mx-auto">{c.toolSubPre} {displayCount}{c.toolSubPost}</p>
            </motion.div>
            <div className="grid sm:grid-cols-2 gap-5">
              {c.toolStack.map((tool, idx) => {
                const meta = TOOL_META[idx];
                return (
                  <motion.div key={tool.name} variants={fadeUp} className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="text-white font-bold text-lg">{tool.name}</div>
                        <div className="text-slate-500 text-xs mt-0.5">{tool.category} — {tool.price}</div>
                      </div>
                      <div className="flex items-center gap-1.5 bg-slate-900/60 border border-white/10 rounded-full px-3 py-1">
                        <div className={`w-2 h-2 rounded-full ${meta.color}`} />
                        <span className="text-xs text-slate-400 font-semibold">{meta.score}% {c.matchLabel}</span>
                      </div>
                    </div>
                    <p className="text-slate-400 text-sm leading-relaxed">{tool.why}</p>
                    <div className="mt-4 h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <motion.div className={`h-full ${meta.color} rounded-full`} initial={{ width: 0 }} whileInView={{ width: `${meta.score}%` }} viewport={{ once: true }} transition={{ duration: 1, delay: 0.3, ease: "easeOut" }} />
                    </div>
                  </motion.div>
                );
              })}
            </div>
            <motion.div variants={fadeUp} className="text-center mt-10">
              <p className="text-slate-500 text-sm">{c.toolFootnotePre} {displayCount}{c.toolFootnotePost}</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="py-20 px-4 border-t border-white/8">
        <div className="max-w-5xl mx-auto">
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}>
            <motion.div variants={fadeUp} className="text-center mb-14">
              <div className="text-sm text-purple-400 font-semibold tracking-widest uppercase mb-3">{c.getEyebrow}</div>
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">{c.getHeadingPre}{currentPrice}{c.getHeadingPost}</h2>
            </motion.div>
            <div className="grid sm:grid-cols-2 gap-5">
              {c.whatYouGet.map((item) => (
                <motion.div key={item.title} variants={fadeUp} className="flex gap-4 p-5 bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl">
                  <div className="w-8 h-8 rounded-xl bg-blue-500/20 border border-blue-500/30 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-[#3b82f6]" />
                  </div>
                  <div>
                    <div className="text-white font-semibold text-sm mb-1">{item.title}</div>
                    <div className="text-slate-400 text-sm leading-relaxed">{item.desc}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
      {/* TRUST STRIP */}
      <section className="py-10 px-4 border-t border-white/8 bg-slate-900/30">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-slate-500 text-sm">{c.guarantee}</p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 border-t border-white/8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <div className="text-sm text-[#3b82f6] font-semibold tracking-widest uppercase mb-3">{c.faqEyebrow}</div>
            <h2 className="text-3xl sm:text-4xl font-black text-white">{c.faqHeading}</h2>
          </div>
          <div className="space-y-3">
            {c.faq.map((item, i) => (
              <details key={i} className="group bg-slate-900/70 border border-white/10 rounded-2xl px-5 py-4 transition-colors hover:border-blue-500/30">
                <summary className="flex items-center justify-between gap-4 cursor-pointer list-none">
                  <span className="text-white font-semibold text-sm leading-snug">{item.q}</span>
                  <span className="text-slate-500 group-open:rotate-45 transition-transform text-xl flex-shrink-0">+</span>
                </summary>
                <p className="text-slate-400 text-sm leading-relaxed mt-3 pt-3 border-t border-white/8">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="py-24 px-4 border-t border-white/8">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <div className="w-16 h-16 rounded-2xl bg-blue-500/20 border border-blue-500/30 flex items-center justify-center mx-auto mb-8">
              <ShoppingBag className="w-8 h-8 text-[#3b82f6]" />
            </div>
            <h2 className="text-4xl sm:text-5xl font-black text-white mb-6">
              {c.bottomHeading1}
              <br />
              <span className="bg-gradient-to-r from-[#3b82f6] to-teal-400 bg-clip-text text-transparent">{c.bottomHeading2}</span>
            </h2>
            <p className="text-slate-400 text-lg mb-10 leading-relaxed">{c.bottomSub}</p>
            <Button onClick={goToAudit} size="lg" className="bg-blue-500 hover:bg-[#3b82f6] text-slate-950 font-black text-xl px-12 py-7 rounded-2xl shadow-xl shadow-blue-500/30 transition-all hover:scale-[1.02] active:scale-[0.98]">
              {c.bottomCta} — ${currentPrice} <ArrowRight className="w-6 h-6 ml-2" />
            </Button>
            <div className="flex items-center justify-center gap-6 mt-8">
              {[{ icon: Clock, text: c.bottomFineReady }, { icon: DollarSign, text: `${c.bottomFineOneTime}${currentPrice}` }, { icon: CheckCircle, text: c.bottomFineNoSub }].map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-2 text-slate-500 text-sm"><Icon className="w-4 h-4" /><span>{text}</span></div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RetailPage;
